/**
 * Data-driven invoice report sections.
 *
 * Add a new line type to INVOICE_REPORT_SECTIONS to support it in Options,
 * Preview, and PDF without rewriting the report generator.
 */

import { formatCurrency } from "./estimateTotals";
import { INVOICE_LINE_TYPE } from "./invoiceLineTypes";
import { LINE_STATUS } from "./invoiceReview";

function getActiveInvoiceLineItems(lineItems = []) {
  return (lineItems || []).filter(
    (item) => (item.line_status || LINE_STATUS.ACTIVE) === LINE_STATUS.ACTIVE,
  );
}

function formatWorkDate(dateIso) {
  if (!dateIso) return null;

  try {
    return new Date(`${dateIso}T12:00:00`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateIso;
  }
}

function buildLabourDetail(item, options) {
  const hours = Number(item.quantity || 0);
  const rate = Number(item.unit_amount || 0);
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours.toFixed(2)} hrs`);
  }

  if (options.showHourlyRates && rate > 0) {
    parts.push(`${formatCurrency(rate)}/hr`);
  }

  return parts.length > 0 ? parts.join(" × ") : item.detail || null;
}

function buildMaterialDetail(item, options) {
  const quantity = Number(item.quantity || 0);
  const unitCost = Number(item.unit_amount || 0);
  const unitLabel = item.unit_label || "ea";
  const parts = [];

  if (options.showQuantities && quantity > 0) {
    parts.push(`${quantity} ${unitLabel}`);
  }

  if (options.showUnitPrices && unitCost > 0) {
    parts.push(`${formatCurrency(unitCost)} /${unitLabel}`);
  }

  if (parts.length > 0) {
    return parts.join(" × ");
  }

  return options.materialDetailLevel === "detailed" ? item.detail || null : null;
}

function buildServiceQtyPriceDetail(item, options) {
  const quantity = Number(item.quantity || 0);
  const unit = Number(item.unit_amount || 0);
  const parts = [];

  if (options.showServiceQuantities && quantity > 0) {
    parts.push(`Qty: ${quantity}`);
  }

  if (options.showServiceUnitPrices && unit > 0) {
    parts.push(`Unit Price: ${formatCurrency(unit)}`);
  }

  return parts.length > 0 ? parts.join("      ") : null;
}

function resolveServiceDescription(item, options) {
  const text = String(item.detail || "").trim();
  if (!text) return null;
  if (options.showServiceDescriptions) return text;
  return null;
}

function sumLineTotals(rows) {
  return rows.reduce((sum, item) => sum + Number(item.line_total || 0), 0);
}

function mapTypedSection({
  lineItems,
  lineType,
  summaryLabel,
  showSection,
  detailLevel,
  mapDetailedRows,
}) {
  const rows = getActiveInvoiceLineItems(lineItems).filter(
    (item) => item.line_type === lineType,
  );

  if (!showSection || rows.length === 0) {
    return {
      mode: "hidden",
      rows: [],
      subtotal: 0,
      summaryLabel,
      lineType,
    };
  }

  const subtotal = sumLineTotals(rows);

  if (detailLevel === "summary") {
    return {
      mode: "summary",
      rows: [],
      subtotal,
      summaryLabel,
      lineType,
    };
  }

  return {
    mode: "detailed",
    rows: mapDetailedRows(rows),
    subtotal,
    summaryLabel,
    lineType,
  };
}

/**
 * Ordered report section definitions.
 * Future types (credit, discount, adjustment, equipment, …) register here.
 */
export const INVOICE_REPORT_SECTIONS = Object.freeze([
  Object.freeze({
    id: "services",
    lineType: INVOICE_LINE_TYPE.SERVICE,
    title: "Services",
    summaryLabel: "Services",
    reportEnabled: true,
    showKey: "showServicesSection",
    detailLevelKey: "serviceDetailLevel",
    defaultShow: true,
    defaultDetailLevel: "summary",
    detailOptions: Object.freeze([
      Object.freeze({
        key: "showServiceDescriptions",
        label: "Show Descriptions",
      }),
      Object.freeze({
        key: "showServiceQuantities",
        label: "Show Quantities",
      }),
      Object.freeze({
        key: "showServiceUnitPrices",
        label: "Show Unit Prices",
      }),
    ]),
    mapPresentation(lineItems, options) {
      return mapTypedSection({
        lineItems,
        lineType: INVOICE_LINE_TYPE.SERVICE,
        summaryLabel: "Services",
        showSection: options.showServicesSection !== false,
        detailLevel: options.serviceDetailLevel || "summary",
        mapDetailedRows: (rows) =>
          rows.map((item) => ({
            name: item.description,
            // Description is independent of qty/unit toggles so both can show.
            description: resolveServiceDescription(item, options),
            detail: buildServiceQtyPriceDetail(item, options),
            notes: item.metadata?.notes || null,
            amount: Number(item.line_total || 0),
          })),
      });
    },
  }),
  Object.freeze({
    id: "labour",
    lineType: INVOICE_LINE_TYPE.LABOUR,
    title: "Labour",
    summaryLabel: "Labour",
    reportEnabled: true,
    showKey: "showLabourSection",
    detailLevelKey: "labourDetailLevel",
    defaultShow: true,
    defaultDetailLevel: "summary",
    detailOptions: Object.freeze([
      Object.freeze({ key: "showEmployeeNames", label: "Show Employee Names" }),
      Object.freeze({ key: "showWorkDates", label: "Show Work Dates" }),
      Object.freeze({ key: "showHourlyRates", label: "Show Hourly Rates" }),
      Object.freeze({ key: "showLabourNotes", label: "Show Notes" }),
    ]),
    mapPresentation(lineItems, options) {
      return mapTypedSection({
        lineItems,
        lineType: INVOICE_LINE_TYPE.LABOUR,
        summaryLabel: "Labour",
        showSection: options.showLabourSection !== false,
        detailLevel: options.labourDetailLevel || "summary",
        mapDetailedRows: (rows) =>
          rows.map((item) => ({
            name: options.showEmployeeNames ? item.description : "Labour",
            date: options.showWorkDates
              ? formatWorkDate(item.metadata?.date_iso)
              : null,
            detail: buildLabourDetail(item, options),
            notes: options.showLabourNotes
              ? item.metadata?.work_completed || item.metadata?.notes || null
              : null,
            amount: Number(item.line_total || 0),
          })),
      });
    },
  }),
  Object.freeze({
    id: "materials",
    lineType: INVOICE_LINE_TYPE.MATERIAL,
    title: "Materials",
    summaryLabel: "Materials",
    reportEnabled: true,
    showKey: "showMaterialsSection",
    detailLevelKey: "materialDetailLevel",
    defaultShow: true,
    defaultDetailLevel: "summary",
    detailOptions: Object.freeze([
      Object.freeze({ key: "showQuantities", label: "Show Quantities" }),
      Object.freeze({ key: "showUnitPrices", label: "Show Unit Prices" }),
    ]),
    mapPresentation(lineItems, options) {
      return mapTypedSection({
        lineItems,
        lineType: INVOICE_LINE_TYPE.MATERIAL,
        summaryLabel: "Materials",
        showSection: options.showMaterialsSection !== false,
        detailLevel: options.materialDetailLevel || "summary",
        mapDetailedRows: (rows) =>
          rows.map((item) => ({
            name: item.description,
            detail: buildMaterialDetail(item, options),
            notes: item.metadata?.notes || null,
            amount: Number(item.line_total || 0),
          })),
      });
    },
  }),
]);

export const INVOICE_REPORT_SECTION_DEFAULT_OPTIONS = Object.freeze(
  INVOICE_REPORT_SECTIONS.reduce((acc, section) => {
    acc[section.showKey] = section.defaultShow;
    acc[section.detailLevelKey] = section.defaultDetailLevel;
    for (const option of section.detailOptions || []) {
      acc[option.key] = false;
    }
    return acc;
  }, {}),
);

/** Active line types present on an invoice (for options UI filtering). */
export function getPresentInvoiceLineTypes(lineItems = []) {
  const types = new Set();
  for (const line of getActiveInvoiceLineItems(lineItems)) {
    if (line?.line_type) types.add(line.line_type);
  }
  return types;
}

/** Report sections that apply to this invoice (have at least one active line). */
export function getApplicableInvoiceReportSections(lineItems = []) {
  const present = getPresentInvoiceLineTypes(lineItems);
  return INVOICE_REPORT_SECTIONS.filter(
    (section) => section.reportEnabled && present.has(section.lineType),
  );
}

/**
 * Build keyed sections + ordered list for Preview/PDF rendering.
 * Keeps legacy keys (labour/materials/services) for backward compatibility.
 */
export function buildInvoiceReportSections(lineItems = [], options = {}) {
  const sections = {};
  const sectionsOrdered = [];

  for (const def of INVOICE_REPORT_SECTIONS) {
    if (!def.reportEnabled) continue;
    const presentation = def.mapPresentation(lineItems, options);
    sections[def.id] = presentation;
    sectionsOrdered.push({
      id: def.id,
      title: def.title,
      section: presentation,
    });
  }

  return { sections, sectionsOrdered };
}

export { getActiveInvoiceLineItems };
